"use client";

import { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { api, ApiError } from "@/lib/api";
import { t } from "@/lib/i18n";

export default function LoginPage() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  // Sign-up is offered here, after a failed attempt, rather than as a second
  // button in the header — someone arriving at the site knows they want in,
  // not yet whether they already have an account.
  const [failed, setFailed] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setFailed(false);
    try {
      const user = await api.login(email, password);
      // Seed the "me" cache with the freshly logged-in user — SiteNav's own
      // useQuery(["me"]) on the landing page caches an error for logged-out
      // visitors, and without this, /projects's useRequireAuth would read
      // that stale error on mount and bounce straight back to /login before
      // its own refetch resolves (looked like "the first login attempt
      // silently fails" — it didn't, the redirect just beat the refetch).
      queryClient.setQueryData(["me"], user);
      router.push("/projects");
    } catch (err) {
      const reachable = err instanceof ApiError;
      // Only a rejected credential means "maybe you have no account yet" —
      // an unreachable server or a rate-limit lockout says nothing about
      // whether the account exists, so don't send those people to sign up.
      setFailed(reachable && err.status === 401);
      toast.error(
        !reachable
          ? t("Could not reach the server")
          : err.status === 429
            ? t("Too many attempts. Try again in a few minutes.")
            : t("Invalid email or password"),
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex flex-1 items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>{t("Neo Dub Studio")}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="email">{t("Email")}</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="password">{t("Password")}</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
            <Button type="submit" disabled={loading} className="mt-2">
              {loading ? t("Signing in...") : t("Sign in")}
            </Button>

            {failed && (
              <div className="rounded-lg border border-border/60 bg-secondary/40 p-3 text-center text-sm text-muted-foreground">
                {t("Don't have an account yet?")}{" "}
                <Link href="/register" className="text-primary hover:underline">
                  {t("Create one")}
                </Link>
              </div>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
